import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { JSX, ReactNode, RefObject } from "react";

import { cn } from "@/lib/cn";

import { IconButton } from "./icon-button";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Visible title. Also the accessible name of the dialog. */
  title: string;
  children: ReactNode;
  /** Footer actions. The primary action goes last, so it sits bottom-right. */
  footer?: ReactNode | undefined;
  /** Width in the scale of `max-w-*` utilities. Defaults to a form-sized dialog. */
  widthClass?: string | undefined;
  /** Element focused on open instead of the first focusable one (the close button). */
  initialFocusRef?: RefObject<HTMLElement> | undefined;
}

/**
 * Modal dialog: title, body, footer with the primary action on the right. The only
 * surface in the product that carries a shadow; it sits over a token overlay.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  children,
  footer,
  widthClass = "max-w-lg",
  initialFocusRef,
}: DialogProps): JSX.Element {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-overlay" />
        <RadixDialog.Content
          onOpenAutoFocus={(event) => {
            if (initialFocusRef?.current) {
              event.preventDefault();
              initialFocusRef.current.focus();
            }
          }}
          className={cn(
            "fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100vh-48px)] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-card border border-line bg-surface shadow-overlay",
            widthClass,
          )}
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <RadixDialog.Title className="text-h2 font-semibold text-ink">{title}</RadixDialog.Title>
            <RadixDialog.Close asChild>
              <IconButton label="Yopish">
                <X strokeWidth={1.5} aria-hidden="true" />
              </IconButton>
            </RadixDialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
          {footer === undefined ? null : (
            <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
